<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contractAttachments',
    path: '/contract-attachments',
    component: 'ContractAttachments',
    title: () => pgettext('ContractAttachments|Title', 'Contract Attachments'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractAttachments',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const gridName = ref('contractAttachments');
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_revision',
      name: $fn.value.pgettext('ContractAttachments', 'Contract'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    {{ $value.contract_number }}\n\
    {{~#if $value.revision_number }}\n\
      /{{ $value.revision_number }}\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'contract_revision.contract_id',
            },
          },
        ],
      },
    },
    {
      field: 'file_name',
      name: $fn.value.pgettext('ContractAttachments', 'File name'),
      useDataContext: true,
      displayFileName: true,
      formatter: 'attachment',
      type: 'command',
    },
    {
      field: 'file_size',
      name: $fn.value.pgettext('ContractAttachments', 'File size'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractAttachments', 'Description'),
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('ContractAttachments', 'Created by'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('browse_contract_attachments');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'ContractAttachments|Title',
        'Contract Attachments'
      ),
      icon: $config.value.attachment.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_48r5xpl5j_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_daexuvftf_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_script_fue7lptmm_value_callable = async (
    $event
  ) => {
    return (
      console.log($event),
      $app.value.attachments.downloadAttachment($event.data.data)
    );
  };
  const act_2248226175642056_script_fue7lptmm_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_fue7lptmm_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_command_15nq3wri1_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'file_name';
  };

  const act_7315092382398562_closeModal_48r5xpl5j =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_48r5xpl5j_getActionArgs,
    });
  const inst_evh_7315092382398562_close_vyai0ar93 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_48r5xpl5j,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_vyai0ar93],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_mxrrdhl9n =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_5f8b595ro =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_mxrrdhl9n,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_5f8b595ro],
    event: 'reload',
  });
  const act_2248226175642056_setData_daexuvftf = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_daexuvftf_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_9ocvjznz3 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_daexuvftf,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_9ocvjznz3],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_script_fue7lptmm = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_fue7lptmm_getActionArgs,
  });
  const inst_evh_2248226175642056_command_15nq3wri1 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_fue7lptmm,
      event: 'command',
      when: inst_evh_2248226175642056_command_15nq3wri1_getWhen,
      displayName: 'script',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_15nq3wri1],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_close_vyai0ar93,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_5f8b595ro,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_9ocvjznz3,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_15nq3wri1,
    inst_evh_2248226175642056_command,
  };

  const evh_7315092382398562_close_vyai0ar93 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_vyai0ar93.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_5f8b595ro = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_5f8b595ro.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_9ocvjznz3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_9ocvjznz3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_15nq3wri1 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_15nq3wri1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractAttachments"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      :name="gridName"
      :headerTitle="documentTitle"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'contract-attachments')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <PageHelp path="/products/products.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
